import React from 'react';
import _get from 'lodash/get';
import {
  AppFullTaskQuery,
  AppFullTaskQueryVariables,
  Task,
  TasksMyTodoCompletableByDateDataQuery,
  TasksMyTodoCompletableByDateDataQueryVariables,
} from 'graphql-common';
import { useLazyQuery } from '@apollo/client/react';
import {
  CompletableTodoTask,
  StoredCompletion,
  SyncProcess,
  SyncStatus,
} from 'components/TaskSyncTracker/types';
import {
  getAllTasksFromDB,
  removeOutdatedTasksFromIndexedDB,
  saveToIndexedDB,
} from 'utils/indexedDBUtils';
import { captureException } from '@lib/utils/captureException';
import { CacheNames } from 'constants/enums';
import {
  storeCompletableTodoTasks,
  storeUtils,
} from 'components/TaskSyncTracker/utils/storeUtils';
import { TIMEOUT_ERROR } from '@lib/constants/fetch';
import isErrorTimeoutError from '@lib/utils/isErrorTimeoutError';
import { logger } from '@lib/services/logger';
import { fetchCompletableTasks } from './fetchCompletableTasks';

export const shouldFetchSyncTasks = ({
  completionTotalCount,
  isOnline,
  isServiceWorkerReady,
  lastTasksFetchTimeRef,
  syncStatus,
  syncTrackerSeconds,
}) => {
  if (
    completionTotalCount ||
    !isServiceWorkerReady ||
    !isOnline ||
    syncStatus === SyncStatus.idle ||
    syncStatus === SyncStatus.success ||
    syncStatus === SyncStatus.error
  ) {
    return false;
  }
  if (!lastTasksFetchTimeRef.current) return true;
  const now = new Date();
  const diff = now.getTime() - lastTasksFetchTimeRef.current.getTime();
  const seconds = syncTrackerSeconds
    ? parseInt(syncTrackerSeconds, 10) * 1000
    : 0;
  return diff >= seconds;
};

type SyncTasksArgs = {
  fetchTask: useLazyQuery.ExecFunction<
    AppFullTaskQuery,
    AppFullTaskQueryVariables
  >;
  fetchTasksMyTodoCompletableByDateData: useLazyQuery.ExecFunction<
    TasksMyTodoCompletableByDateDataQuery,
    TasksMyTodoCompletableByDateDataQueryVariables
  >;
  isViewLocked: boolean;
  setExpectedSyncItems: (v: CompletableTodoTask[] | StoredCompletion[]) => void;
  setFailedSyncItems: (v: CompletableTodoTask[] | StoredCompletion[]) => void;
  setFetchedSyncItems: React.Dispatch<
    React.SetStateAction<CompletableTodoTask[] | StoredCompletion[]>
  >;
  setSyncStatus: React.Dispatch<React.SetStateAction<SyncStatus>>;
  syncTrackerDays: number;
  onProcessStart: (v: SyncProcess) => void;
  onProcessEnd: () => void;
};

export const syncTasks = async ({
  fetchTask,
  fetchTasksMyTodoCompletableByDateData,
  isViewLocked,
  setExpectedSyncItems,
  setFailedSyncItems,
  setFetchedSyncItems,
  setSyncStatus,
  syncTrackerDays,
  onProcessStart,
  onProcessEnd,
}: SyncTasksArgs) => {
  const startTime = Date.now();

  try {
    logger.info('Starting tasks sync', {
      'sync.type': 'tasks',
      'sync.days_ahead': syncTrackerDays,
    });

    onProcessStart(SyncProcess.tasks);
    const response = await fetchCompletableTasks(
      fetchTasksMyTodoCompletableByDateData,
      syncTrackerDays,
    );
    const { error } = response;
    const isTimeoutError = isErrorTimeoutError(error);
    const completableTodoTasks: CompletableTodoTask[] = _get(
      response,
      ['data', 'data'],
      [],
    ).map(({ id, offlineImportantDataDigest }) => ({
      id,
      offlineImportantDataDigest,
    }));

    logger.debug('Fetched completable tasks list', {
      'sync.server_task_count': completableTodoTasks.length,
      'sync.is_timeout': isTimeoutError,
    });

    if (isTimeoutError) {
      logger.error('Tasks sync aborted due to timeout', {
        'sync.type': 'tasks',
        'sync.error': TIMEOUT_ERROR,
      });
      onProcessEnd();
      throw new Error(TIMEOUT_ERROR);
    }

    const localCompletableTodoTasks = storeUtils();

    let tasksToFetch: CompletableTodoTask[] = [];
    let cachedTasks: Task[] = [];

    if (!localCompletableTodoTasks || localCompletableTodoTasks.length === 0) {
      tasksToFetch = completableTodoTasks;
    } else if (localCompletableTodoTasks.length) {
      cachedTasks = await getAllTasksFromDB();
      tasksToFetch = completableTodoTasks.filter(
        (completableTodoTask: CompletableTodoTask) => {
          const localTask = localCompletableTodoTasks.find(
            (item) =>
              item.id === completableTodoTask.id &&
              cachedTasks.findIndex(
                ({ id: cachedId }) => cachedId === item.id,
              ) > -1,
          );
          return (
            !localTask ||
            localTask.offlineImportantDataDigest !==
              completableTodoTask.offlineImportantDataDigest
          );
        },
      );
    }

    // Determine outdated tasks
    const outdatedTasksIds = cachedTasks
      .filter(
        (cachedTask) =>
          !completableTodoTasks.find(
            (completableTask) => completableTask.id === cachedTask.id,
          ),
      )
      .map(({ id }) => id);

    logger.debug('Determined tasks requiring sync', {
      'sync.tasks_to_fetch': tasksToFetch.length,
      'sync.cached_tasks': cachedTasks.length,
      'sync.outdated_tasks': outdatedTasksIds.length,
    });

    if (outdatedTasksIds.length > 0) {
      logger.debug('Removing outdated tasks from cache', {
        'sync.outdated_count': outdatedTasksIds.length,
      });
    }

    await removeOutdatedTasksFromIndexedDB(outdatedTasksIds);

    setExpectedSyncItems(tasksToFetch);

    if (tasksToFetch.length) {
      setSyncStatus(SyncStatus.idle);
    } else {
      onProcessEnd();
    }

    const newFetchedTasks: CompletableTodoTask[] = [];
    const newFailedTasks: CompletableTodoTask[] = [];

    // eslint-disable-next-line no-restricted-syntax
    for (const task of tasksToFetch) {
      const progress = `${newFetchedTasks.length + newFailedTasks.length + 1}/${tasksToFetch.length}`;

      logger.debug('Fetching task for offline cache', {
        'sync.task_id': task.id,
        'sync.progress': progress,
      });

      try {
        // eslint-disable-next-line no-await-in-loop
        const taskResponse = await fetchTask({
          variables: { id: task.id, isViewLocked },
        });
        const taskData = _get(taskResponse, ['data', 'data']);
        if (!taskData) {
          newFailedTasks.push(task);
          setFailedSyncItems([...newFailedTasks]);

          logger.error('Failed to fetch task - no data returned', {
            'sync.task_id': task.id,
          });
        } else {
          saveToIndexedDB(CacheNames.tasks, {
            ...taskData,
          });
          newFetchedTasks.push(task);
          setFetchedSyncItems([...newFetchedTasks]);

          logger.debug('Task cached successfully', {
            'sync.task_id': task.id,
          });
        }
      } catch (fetchError) {
        newFailedTasks.push(task);
        setFailedSyncItems([...newFailedTasks]);

        logger.error('Failed to fetch task', {
          'sync.task_id': task.id,
          'sync.error':
            fetchError instanceof Error
              ? fetchError.message
              : String(fetchError),
        });
      }
    }

    // All tasks processed, set status
    const hasErrors = newFailedTasks.length > 0;
    const durationMs = Date.now() - startTime;

    if (hasErrors) {
      logger.warn('Tasks sync completed with errors', {
        'sync.type': 'tasks',
        'sync.fetched_count': newFetchedTasks.length,
        'sync.failed_count': newFailedTasks.length,
        'sync.duration_ms': durationMs,
      });
    } else {
      logger.info('Tasks sync completed', {
        'sync.type': 'tasks',
        'sync.fetched_count': newFetchedTasks.length,
        'sync.duration_ms': durationMs,
      });
    }

    setSyncStatus(hasErrors ? SyncStatus.error : SyncStatus.success);

    storeCompletableTodoTasks(completableTodoTasks);
  } catch (error) {
    const durationMs = Date.now() - startTime;

    logger.error('Tasks sync failed', {
      'sync.type': 'tasks',
      'sync.error': error instanceof Error ? error.message : String(error),
      'sync.duration_ms': durationMs,
    });

    if (typeof error === 'string') {
      captureException(new Error(error));
    } else if (error instanceof Error) {
      captureException(error);
    }
    setSyncStatus(SyncStatus.error);
  }
};
